import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ApplicationService } from '../../services/ApplicationService';
import { JobService } from '../../services/JobService';
import { BusinessService } from '../../services/BusinessService';
import { collection, query, where, orderBy, limit, getDocs, getDoc, doc } from 'firebase/firestore';
import { firestore } from '../../firebase/config';
import { toast } from 'react-hot-toast';

const Updates = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [allApplications, setAllApplications] = useState([]); // 所有原始数据
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  // 根据activeFilter过滤应用数据
  const applications = React.useMemo(() => {
    if (activeFilter === 'all') {
      return allApplications;
    }
    return allApplications.filter(app => app.status === activeFilter);
  }, [allApplications, activeFilter]);

  // Fetch user applications and related data (只在用户变化时获取)
  useEffect(() => {
    if (currentUser) {
      fetchApplicationUpdates();
    }
  }, [currentUser]);

  const fetchApplicationUpdates = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      console.log('🔍 Fetching applications for user:', currentUser.uid);
      
      // Use the same simple query as DashboardHome.jsx (without orderBy to avoid index issues)
      let applicationsQuery = query(
        collection(firestore, 'jobApplications'),
        where('userId', '==', currentUser.uid),
        limit(50)
      );
      
      console.log('📊 Executing Firestore query...');
      const applicationsSnapshot = await getDocs(applicationsQuery);
      console.log(`📋 Found ${applicationsSnapshot.size} total applications`);
      
      if (applicationsSnapshot.empty) {
        console.log('❌ No applications found in database');
        setAllApplications([]);
        return;
      }
      
      // Convert to array of application objects
      let userApplications = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('📝 Raw applications data:', userApplications);
      
      // Log the first application to see available fields
      if (userApplications.length > 0) {
        console.log('🔍 First application fields:', Object.keys(userApplications[0]));
        console.log('🔍 First application data:', userApplications[0]);
        
        // Log all applications to see which ones have businessName
        userApplications.forEach((app, index) => {
          console.log(`🔍 Application ${index + 1} businessName:`, {
            id: app.id,
            businessName: app.businessName,
            hasBusinessName: !!app.businessName,
            businessId: app.businessId
          });
        });
      }
      
      // Sort by appliedAt client-side (to avoid needing a composite index)
      userApplications.sort((a, b) => {
        const aTime = a.appliedAt || a.createdAt || a.updatedAt;
        const bTime = b.appliedAt || b.createdAt || b.updatedAt;
        
        // Handle Firestore timestamps and regular dates
        const aDate = aTime?.seconds ? new Date(aTime.seconds * 1000) : 
                     aTime?.toDate ? aTime.toDate() : 
                     new Date(aTime || 0);
        const bDate = bTime?.seconds ? new Date(bTime.seconds * 1000) : 
                     bTime?.toDate ? bTime.toDate() : 
                     new Date(bTime || 0);
        
        return bDate - aDate; // Most recent first
      });
      
            console.log(`🔧 Processing ${userApplications.length} applications...`);
      
      // BATCH FETCHING PATTERN - Same as DashboardHome.jsx
      // Step 1: Collect all unique businessIds and jobIds
      const businessIds = new Set();
      const jobIds = new Set();
      
      userApplications.forEach(application => {
        if (application.businessId) {
          businessIds.add(application.businessId);
        }
        if (application.jobId) {
          jobIds.add(application.jobId);
        }
      });
      
      console.log(`Found ${businessIds.size} unique businesses and ${jobIds.size} unique jobs to fetch`);
      
      // Step 2: Batch fetch all business data
      const businessDataMap = {};
      await Promise.all(
        Array.from(businessIds).map(async (businessId) => {
          try {
            const businessDoc = await getDoc(doc(firestore, 'businesses', businessId));
            if (businessDoc.exists()) {
              businessDataMap[businessId] = businessDoc.data();
              console.log(`✅ Business data fetched for ${businessId}:`, businessDoc.data().name);
            }
          } catch (err) {
            console.error(`❌ Error fetching business data for ID ${businessId}:`, err);
          }
        })
      );
      
      // Step 3: Batch fetch all job data
      const jobDataMap = {};
      await Promise.all(
        Array.from(jobIds).map(async (jobId) => {
          try {
            const jobDoc = await getDoc(doc(firestore, 'jobs', jobId));
            if (jobDoc.exists()) {
              jobDataMap[jobId] = jobDoc.data();
              console.log(`✅ Job data fetched for ${jobId}:`, jobDoc.data().title);
            }
          } catch (err) {
            console.error(`❌ Error fetching job data for ID ${jobId}:`, err);
          }
        })
      );
      
      // Step 4: Process applications using the data maps
      const enrichedApplications = userApplications.map((application, index) => {
        const jobData = jobDataMap[application.jobId] || null;
        const businessData = businessDataMap[application.businessId] || null;
        
        console.log(`🔧 Processing application ${index + 1} with fetched data:`, {
          applicationId: application.id,
          jobTitle: jobData?.title || 'Not found',
          businessName: businessData?.name || 'Not found'
        });
        
        // Create final data objects - Same pattern as DashboardHome
        const finalJobData = jobData ? {
          id: application.jobId,
          title: jobData.title || 'Unknown Position'
        } : { 
          title: application.jobTitle || 'Unknown Position', 
          id: application.jobId 
        };
        
        const finalBusinessData = businessData ? {
          id: application.businessId,
          name: businessData.name || 'Unknown Business'
        } : { 
          name: application.businessName || 'Unknown Business', 
          id: application.businessId 
        };
        
        const enrichedApplication = {
          ...application,
          jobData: finalJobData,
          businessData: finalBusinessData,
          time: formatTimeAgo(application.updatedAt || application.appliedAt),
          updateType: getUpdateType(application.status),
          updateMessage: getUpdateMessage(application, finalJobData, finalBusinessData)
        };
        
        console.log(`✅ Enriched application ${index + 1}:`, {
          id: enrichedApplication.id,
          jobTitle: enrichedApplication.jobData.title,
          businessName: enrichedApplication.businessData.name,
          status: enrichedApplication.status
        });
        
        return enrichedApplication;
      });
      
      console.log(`🎯 Final result: ${enrichedApplications.length} enriched applications`);
      setAllApplications(enrichedApplications);
      
    } catch (error) {
      console.error('❌ Error fetching application updates:', error);
      toast.error('Failed to load application updates');
      
      // Try fallback method using ApplicationService
      console.log('🔄 Trying fallback method with ApplicationService...');
      try {
        const fallbackApplications = await ApplicationService.getUserApplications(currentUser.uid, { limit: 50 });
        console.log(`🔄 Fallback method found ${fallbackApplications.length} applications`);
        
        // Process fallback applications with minimal enrichment (不在这里过滤，在前端过滤)
        const processedFallback = fallbackApplications.map(application => ({
          ...application,
          jobData: { title: `Job ${application.jobId?.substring(0, 8)}...` || 'Position', id: application.jobId },
          businessData: { name: `Business ${application.businessId?.substring(0, 8)}...` || 'Company', id: application.businessId },
          time: formatTimeAgo(application.updatedAt || application.appliedAt),
          updateType: getUpdateType(application.status),
          updateMessage: `Your application is ${application.status || 'pending'}`
        }));
        
        setAllApplications(processedFallback);
        console.log(`✅ Fallback method successful: ${processedFallback.length} applications loaded`);
      } catch (fallbackError) {
        console.error('❌ Fallback method also failed:', fallbackError);
        setAllApplications([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getUpdateType = (status) => {
    switch (status) {
      case 'pending': return 'application';
      case 'reviewing': return 'info';
      case 'interviewed': return 'interview';
      case 'hired': return 'success';
      case 'rejected': return 'info';
      default: return 'application';
    }
  };

  const getUpdateMessage = (application, jobData, businessData) => {
    const jobTitle = jobData?.title || 'position';
    const businessName = businessData?.name || 'the company';
    
    switch (application.status) {
      case 'pending':
        return `Your application for ${jobTitle} at ${businessName} is under review.`;
      case 'reviewing':
        return `Your application for ${jobTitle} at ${businessName} is being reviewed by the employer.`;
      case 'interviewed':
        return `You have been interviewed for the ${jobTitle} position at ${businessName}.`;
      case 'hired':
        return `Congratulations! You have been hired for the ${jobTitle} position at ${businessName}.`;
      case 'rejected':
        return `Your application for ${jobTitle} at ${businessName} was not selected this time.`;
      default:
        return `Your application for ${jobTitle} at ${businessName} has been updated.`;
    }
  };

  const handleViewDetails = (application) => {
    // Navigate to job details page
    if (application.jobData?.id) {
      navigate(`/jobs/${application.jobData.id}`);
    } else {
      navigate('/dashboard/jobs');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'hired':
        return (
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'rejected':
        return (
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'reviewing':
        return (
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'interviewed':
        return (
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
          </div>
        );
      default: // pending and others
        return (
          <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
          </div>
        );
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      reviewing: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Under Review' },
      interviewed: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Interviewed' },
      hired: { bg: 'bg-green-100', text: 'text-green-800', label: 'Hired' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Not Selected' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 172800) {
      return 'Yesterday';
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // 计数器基于所有原始数据，不受过滤影响
  const applicationCounts = React.useMemo(() => ({
    all: allApplications.length,
    pending: allApplications.filter(app => app.status === 'pending').length,
    reviewing: allApplications.filter(app => app.status === 'reviewing').length,
    interviewed: allApplications.filter(app => app.status === 'interviewed').length,
    hired: allApplications.filter(app => app.status === 'hired').length,
    rejected: allApplications.filter(app => app.status === 'rejected').length
  }), [allApplications]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Application Updates</h1>
          <p className="text-sm text-gray-600 mt-1">
            Track the status of your job applications
          </p>
        </div>
        <button 
          onClick={fetchApplicationUpdates}
          className="px-4 py-2 text-[#FF8C00] bg-[#FF8C00]/10 rounded-lg hover:bg-[#FF8C00]/20 transition-colors font-medium"
        >
          Refresh
        </button>
      </div>
      
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-medium text-gray-800">Your Applications</h2>
          <div className="flex gap-1">
            <button 
              onClick={() => setActiveFilter('all')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeFilter === 'all' 
                  ? 'bg-[#FF8C00] text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              All ({applicationCounts.all})
            </button>
            <button 
              onClick={() => setActiveFilter('pending')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeFilter === 'pending' 
                  ? 'bg-[#FF8C00] text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
                             }`}
             >
               Pending ({applicationCounts.pending})
             </button>
                         <button 
              onClick={() => setActiveFilter('reviewing')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeFilter === 'reviewing' 
                  ? 'bg-[#FF8C00] text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Reviewing ({applicationCounts.reviewing})
            </button>
            <button 
              onClick={() => setActiveFilter('interviewed')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeFilter === 'interviewed' 
                  ? 'bg-[#FF8C00] text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Interviewed ({applicationCounts.interviewed})
            </button>
            <button 
              onClick={() => setActiveFilter('hired')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeFilter === 'hired' 
                  ? 'bg-[#FF8C00] text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Hired ({applicationCounts.hired})
            </button>
             <button 
               onClick={() => setActiveFilter('rejected')}
               className={`px-3 py-1 text-sm rounded-md transition-colors ${
                 activeFilter === 'rejected' 
                   ? 'bg-[#FF8C00] text-white' 
                   : 'text-gray-600 hover:bg-gray-100'
               }`}
             >
               Rejected ({applicationCounts.rejected})
             </button>
           </div>
         </div>
         
         {loading ? (
           <div className="p-8 text-center">
             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8C00] mx-auto"></div>
             <p className="text-gray-600 mt-2">Loading your applications...</p>
           </div>
         ) : applications.length === 0 ? (
           <div className="p-8 text-center">
             <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
               <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                 <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                 <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
               </svg>
             </div>
             <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
             <p className="text-gray-600 mb-4">
               {activeFilter === 'all' 
                 ? "You haven't applied to any jobs yet. Start exploring opportunities!" 
                 : `No ${activeFilter} applications found.`}
             </p>
             <button
               onClick={() => navigate('/dashboard/jobs')}
               className="px-4 py-2 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors"
             >
               Find Jobs
             </button>
           </div>
         ) : (
           <div className="divide-y divide-gray-100">
             {applications.map(application => (
               <div 
                 key={application.id} 
                 className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                 onClick={() => handleViewDetails(application)}
               >
                 <div className="flex items-start gap-3">
                   <div className="flex-shrink-0 mt-1">
                     {getStatusIcon(application.status)}
                   </div>
                   <div className="flex-grow">
                     <div className="flex justify-between items-start">
                       <div className="flex-grow">
                         <h3 className="font-medium text-gray-800">{application.jobData.title}</h3>
                         <p className="text-sm text-gray-600">{application.businessData.name}</p>
                       </div>
                       <div className="flex flex-col items-end gap-2">
                         <span className="text-xs text-gray-500">{application.time}</span>
                         {getStatusBadge(application.status)}
                       </div>
                     </div>
                     <p className="text-gray-600 mt-2 text-sm">{application.updateMessage}</p>
                     <div className="mt-3 flex justify-between items-center">
                       <button 
                         onClick={(e) => {
                           e.stopPropagation();
                           handleViewDetails(application);
                         }}
                         className="text-sm text-[#FF8C00] hover:underline"
                       >
                         View Job Details
                       </button>
                       <span className="text-xs text-gray-400">
                         Applied {formatTimeAgo(application.appliedAt)}
                       </span>
                     </div>
                   </div>
                 </div>
               </div>
             ))}
           </div>
         )}
       </div>
     </div>
   );
 };
 
 export default Updates;